import { useEffect, useState } from 'react';
import { getLead, getLeadMessages, sendLeadMessage } from '../services/api.js';
import { messagingHelp } from '../utils/helpContent';
import HelpTooltip from './HelpTooltip';

const LeadMessage = ({ leadId, authToken }) => {
  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageType, setMessageType] = useState('sms');
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMessages = async () => {
    try {
      const response = await getLeadMessages(authToken, leadId);
      setMessages(response.results || []);
    } catch (err) {
      setError(err.response?.status === 401 ? 'Unauthorized: Please log in again.' : 'Failed to fetch messages');
    }
  };

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const leadData = await getLead(authToken, leadId);
        setLead(leadData);
      } catch (err) {
        setError(err.response?.status === 401 ? 'Unauthorized: Please log in again.' : 'Failed to fetch lead details');
      }
    };

    fetchLead();
    fetchMessages();

    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [leadId, authToken]);

  const handleSendMessage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sendLeadMessage(authToken, leadId, messageType, content);
      setMessages([response, ...messages]);
      setContent('');
    } catch (err) {
      setError(err.response?.status === 401 ? 'Unauthorized: Please log in again.' : 'Failed to send message');
    }
    setLoading(false);
  };

  if (!lead) return <div className="flex items-center justify-center min-h-screen text-gray-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 flex-1">
            {lead.first_name} {lead.last_name}
          </h2>
          <HelpTooltip
            title={messagingHelp.messagingOverview.title}
            content={messagingHelp.messagingOverview.content}
            size="lg"
            position="top-left"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 bg-accent-teal-subtle p-4 rounded-md">
          <p className="text-gray-700">
            <strong className="font-medium">Phone:</strong>{" "}
            {lead.phone || "N/A"}
          </p>
          <p className="text-gray-700">
            <strong className="font-medium">Email:</strong>{" "}
            {lead.email || "N/A"}
          </p>
          <p className="text-gray-700">
            <strong className="font-medium">Type:</strong>{" "}
            {lead.type ? lead.type.replace("_", " ").toUpperCase() : "N/A"}
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              Message Type
              <HelpTooltip
                title={messagingHelp.messageTypes.title}
                content={messagingHelp.messageTypes.content}
                className="ml-1"
                size="md"
              />
            </label>
            <select
              value={messageType}
              onChange={(e) => setMessageType(e.target.value)}
              className="w-full p-2 border border-border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-background disabled:cursor-not-allowed"
              disabled={!lead.phone && !lead.email}
            >
              {lead.phone && <option value="sms">SMS</option>}
              {lead.email && <option value="email">Email</option>}
            </select>
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
              Message
              <HelpTooltip
                title={messagingHelp.messageComposition.title}
                content={messagingHelp.messageComposition.content}
                className="ml-1"
                size="md"
              />
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full p-3 border border-border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-background disabled:cursor-not-allowed"
              rows="4"
              placeholder="Type your message..."
              disabled={!lead.phone && !lead.email}
            />
          </div>

          <button
            onClick={handleSendMessage}
            disabled={loading || !content || (!lead.phone && !lead.email)}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-primary focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Sending..." : "Send Message"}
          </button>

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>

        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            Message History
            <HelpTooltip
              title={messagingHelp.messageDirection.title}
              content={messagingHelp.messageDirection.content}
              className="ml-2"
              size="md"
            />
          </h3>
          {messages.length === 0 ? (
            <p className="text-gray-500">No messages yet.</p>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`p-4 rounded-lg shadow-sm ${
                    message.direction === "inbound"
                      ? "bg-accent-teal-subtle ml-4 mr-12"
                      : "bg-accent-teal-light-bg ml-12 mr-4"
                  }`}
                >
                  <p className="text-sm text-gray-700">
                    <strong className="font-medium">Type:</strong>{" "}
                    {message.message_type.toUpperCase()}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong className="font-medium">Direction:</strong>{" "}
                    {message.direction === "inbound" ? "Received" : "Sent"}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong className="font-medium">Time:</strong>{" "}
                    {new Date(message.sent_at).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong className="font-medium">Content:</strong>{" "}
                    <span className="ph-mask">{message.content}</span>
                  </p>
                  <div className="flex items-center text-sm text-gray-700">
                    <strong className="font-medium">Status:</strong>
                    <span className="ml-1">
                      {message.message_type === "sms" ? message.status : "N/A"}
                    </span>
                    <HelpTooltip
                      title={messagingHelp.messageStatuses.title}
                      content={messagingHelp.messageStatuses.content}
                      className="ml-1"
                      size="sm"
                    />
                  </div>
                  {message.error_message && (
                    <p className="text-sm text-red-500">
                      <strong className="font-medium">Error:</strong>{" "}
                      {message.error_message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadMessage;